/**
 * CreatorsLanding — landing cinematográfica en /creators para captar creadores de YouTube.
 * Coexiste con Landing.jsx (que sigue montada en `/`). Diseño oscuro Netflix-docs: paleta oro
 * anticuado #C9A961 + off-white #F5F1E8 + deep black #0A0A0A. Playfair Display + Inter.
 *
 * Estructura: Hero → Cómo funciona → Galería resaltes → Motor multi-fuente → Antes/Después
 * → Precios → Testimonios → FAQ → CTA final → Footer. Todo i18n (ES/EN).
 *
 * Capturas reales de la app en /public/landing-shots/*.png (generadas vía Playwright).
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useGoogleLogin } from "@react-oauth/google";
import { useAuth } from "../context/AuthContext";
import { fmtCredits } from "../lib/api";
import {
  ArrowRight, Play, Sparkles, Wand2, Film, Layers, Palette, Zap, Globe,
  ShieldCheck, ChevronDown, Check, Star, Youtube, Instagram, Twitter, Mail,
} from "lucide-react";

// Paleta cinematográfica (mirrors design_guidelines.json)
const C = {
  bg: "#0A0A0A", surf: "#121212", cream: "#F5F1E8", muted: "#A8A29A", gold: "#C9A961",
};

// Fuentes: cargamos Playfair Display, Inter y IBM Plex Mono desde Google Fonts.
// Se inyecta solo al montar esta página para no impactar el resto de la app.
function useLoadFonts() {
  useEffect(() => {
    const links = [
      "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Inter:wght@400;500;600;700;800&family=IBM+Plex+Mono:wght@400;500&display=swap",
    ];
    const els = links.map((href) => {
      const l = document.createElement("link");
      l.rel = "stylesheet"; l.href = href;
      document.head.appendChild(l);
      return l;
    });
    return () => { els.forEach((l) => l.remove()); };
  }, []);
}

// Hairline dorada — divisor firma de la app
const Hairline = ({ className = "" }) => (
  <div className={`h-px w-full ${className}`} style={{ backgroundColor: "rgba(201,169,97,0.25)" }} />
);

// Kicker (etiqueta pequeña oro sobre título de sección)
const Kicker = ({ children }) => (
  <div className="text-xs font-semibold tracking-[0.36em] uppercase mb-4"
    style={{ color: C.gold, fontFamily: "'IBM Plex Mono', monospace" }}>{children}</div>
);

const H2 = ({ children, className = "" }) => (
  <h2 className={`text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight ${className}`}
    style={{ fontFamily: "'Playfair Display', serif", color: C.cream }}>{children}</h2>
);

const Lead = ({ children, className = "" }) => (
  <p className={`text-lg leading-relaxed max-w-2xl ${className}`}
    style={{ color: C.muted }}>{children}</p>
);

// Reveal on scroll
const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.19, 1, 0.22, 1] }}
    className={className}
  >{children}</motion.div>
);

// Sticky header con glassmorphism
function Header({ lang, setLang, t, startLogin, loginBusy }) {
  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl border-b"
      style={{ backgroundColor: "rgba(10,10,10,0.7)", borderColor: "rgba(168,162,154,0.15)" }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/creators" data-testid="header-logo" className="flex items-center gap-2">
          <img src="/ytmethod_transparent.png" alt="YTMethod" className="h-9 w-auto" style={{ filter: "brightness(0) invert(1)" }} />
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm font-medium" style={{ color: C.muted }}>
          <a href="#how" className="hover:text-white transition-colors" data-testid="nav-how">{t("nav.how")}</a>
          <a href="#highlights" className="hover:text-white transition-colors" data-testid="nav-highlights">{t("nav.highlights")}</a>
          <a href="#sources" className="hover:text-white transition-colors" data-testid="nav-sources">{t("nav.sources")}</a>
          <a href="#pricing" className="hover:text-white transition-colors" data-testid="nav-pricing">{t("nav.pricing")}</a>
          <a href="#faq" className="hover:text-white transition-colors" data-testid="nav-faq">{t("nav.faq")}</a>
        </nav>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 text-xs font-semibold tracking-widest px-2.5 py-1 border rounded"
            style={{ borderColor: "rgba(201,169,97,0.4)", fontFamily: "'IBM Plex Mono', monospace" }}>
            <button
              data-testid="lang-toggle-es"
              onClick={() => setLang("es")}
              className="px-1 transition-colors"
              style={{ color: lang === "es" ? C.gold : C.muted, fontWeight: lang === "es" ? 700 : 400 }}>
              ES
            </button>
            <span style={{ color: C.muted, opacity: 0.5 }}>|</span>
            <button
              data-testid="lang-toggle-en"
              onClick={() => setLang("en")}
              className="px-1 transition-colors"
              style={{ color: lang === "en" ? C.gold : C.muted, fontWeight: lang === "en" ? 700 : 400 }}>
              EN
            </button>
          </div>
          <button type="button" onClick={startLogin} disabled={loginBusy} data-testid="cta-header" className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold hover:scale-[1.02] transition-transform disabled:opacity-60"
            style={{ backgroundColor: C.gold, color: C.bg }}>
            {t("nav.cta")} <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </header>
  );
}

// Hero
function Hero({ t, startLogin, loginBusy }) {
  return (
    <section className="relative pt-32 pb-20 lg:pt-36 lg:pb-20 overflow-hidden">
      {/* Grano cinematográfico */}
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{ backgroundImage: "url('https://images.pexels.com/photos/18599094/pexels-photo-18599094.jpeg')", backgroundSize: "cover", mixBlendMode: "overlay" }} />
      {/* Radial glow */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: `radial-gradient(circle at 20% 30%, rgba(201,169,97,0.10) 0%, transparent 55%)` }} />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1.15fr_1fr] gap-16 items-center">
        <div>
          <Reveal>
            <Kicker>{t("hero.kicker")}</Kicker>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.02] tracking-tight mb-6"
              style={{ fontFamily: "'Playfair Display', serif", color: C.cream }}>
              {t("hero.title.line1")}<br />
              <span style={{ color: C.gold, fontStyle: "italic" }}>{t("hero.title.line2")}</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-xl leading-relaxed mb-8 max-w-xl" style={{ color: C.muted }}>{t("hero.subtitle")}</p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="flex flex-wrap gap-4">
              <button type="button" onClick={startLogin} disabled={loginBusy} data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full text-base font-bold hover:scale-[1.02] transition-transform shadow-lg disabled:opacity-60"
                style={{ backgroundColor: C.gold, color: C.bg, boxShadow: `0 12px 40px -12px ${C.gold}` }}>
                <Wand2 size={18} /> {t("hero.ctaPrimary")}
              </button>
              <a href="#how" data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full text-base font-semibold border transition-colors"
                style={{ color: C.cream, borderColor: "rgba(245,241,232,0.25)" }}>
                <Play size={16} /> {t("hero.ctaSecondary")}
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.4}>
            <div className="mt-10 flex items-center gap-6 text-sm" style={{ color: C.muted }}>
              <div className="flex items-center gap-1.5"><Check size={14} style={{ color: C.gold }} /> {t("hero.feature1")}</div>
              <div className="flex items-center gap-1.5"><Check size={14} style={{ color: C.gold }} /> {t("hero.feature2")}</div>
              <div className="flex items-center gap-1.5"><Check size={14} style={{ color: C.gold }} /> {t("hero.feature3")}</div>
            </div>
          </Reveal>
        </div>
        {/* Screenshot con marco cine */}
        <Reveal delay={0.3}>
          <div className="relative">
            <div className="absolute -inset-6 rounded-2xl opacity-30 blur-3xl" style={{ backgroundColor: C.gold }} />
            <div className="relative rounded-xl overflow-hidden border shadow-2xl"
              style={{ borderColor: "rgba(201,169,97,0.3)", boxShadow: "0 40px 80px -20px rgba(0,0,0,0.9)" }}>
              <img src="/landing-shots/03-oneclick-modal.png" alt="OneClick modal" className="w-full block" />
              {/* Cinemascope bars */}
              <div className="absolute inset-x-0 top-0 h-4" style={{ backgroundColor: C.bg }} />
              <div className="absolute inset-x-0 bottom-0 h-4" style={{ backgroundColor: C.bg }} />
            </div>
            <div className="absolute -bottom-6 -right-6 px-4 py-3 rounded-xl border backdrop-blur-lg"
              style={{ backgroundColor: "rgba(10,10,10,0.85)", borderColor: "rgba(201,169,97,0.3)" }}>
              <div className="text-xs font-semibold tracking-widest uppercase" style={{ color: C.gold, fontFamily: "'IBM Plex Mono', monospace" }}>{t("hero.badgeLabel")}</div>
              <div className="text-sm font-bold" style={{ color: C.cream }}>{t("hero.badgeValue")}</div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// Cómo funciona
const STEPS_ICONS = [Wand2, Sparkles, Layers, Film];
function HowItWorks({ t }) {
  const steps = t("how.steps", { returnObjects: true });
  const shots = ["/landing-shots/03-oneclick-modal.png", "/landing-shots/05-editor-guion.png", "/landing-shots/06-editor-broll.png", "/landing-shots/02-channel-videos.png"];
  return (
    <section id="how" className="py-24 lg:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Kicker>{t("how.kicker")}</Kicker>
          <H2 className="mb-4 max-w-3xl">{t("how.title")}</H2>
          <Lead className="mb-16">{t("how.subtitle")}</Lead>
        </Reveal>
        <div className="space-y-24 lg:space-y-32">
          {steps.map((s, i) => {
            const Icon = STEPS_ICONS[i] || Wand2;
            const reverse = i % 2 === 1;
            return (
              <Reveal key={i} delay={0.05}>
                <div className={`grid lg:grid-cols-2 gap-12 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
                  <div>
                    <div className="text-6xl font-black mb-4 leading-none tabular-nums"
                      style={{ color: "rgba(201,169,97,0.3)", fontFamily: "'IBM Plex Mono', monospace" }}>
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="inline-flex items-center gap-2 mb-4" style={{ color: C.gold }}>
                      <Icon size={20} />
                      <span className="text-xs font-semibold tracking-widest uppercase" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{s.tag}</span>
                    </div>
                    <h3 className="text-3xl lg:text-4xl font-black mb-4 leading-tight"
                      style={{ fontFamily: "'Playfair Display', serif", color: C.cream }}>{s.title}</h3>
                    <p className="text-lg leading-relaxed" style={{ color: C.muted }}>{s.desc}</p>
                  </div>
                  <div className="relative">
                    <div className="rounded-xl overflow-hidden border" style={{ borderColor: "rgba(168,162,154,0.2)" }}>
                      <img src={shots[i]} alt={s.title} className="w-full block" data-testid={`how-shot-${i}`} />
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// Galería de resaltes — marquee infinito con miniaturas ilustrativas de plantillas
function HighlightsGallery({ t }) {
  const highlights = t("highlights.items", { returnObjects: true });
  // Duplicamos para lograr un loop continuo
  const loop = [...highlights, ...highlights];
  return (
    <section id="highlights" className="py-24 lg:py-32 relative overflow-hidden border-y"
      style={{ borderColor: "rgba(168,162,154,0.15)", backgroundColor: C.surf }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 mb-14">
        <Reveal>
          <Kicker>{t("highlights.kicker")}</Kicker>
          <H2 className="mb-4 max-w-3xl">{t("highlights.title")}</H2>
          <Lead>{t("highlights.subtitle")}</Lead>
        </Reveal>
      </div>
      {/* Marquee */}
      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
          style={{ background: `linear-gradient(to right, ${C.surf}, transparent)` }} />
        <div className="absolute right-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
          style={{ background: `linear-gradient(to left, ${C.surf}, transparent)` }} />
        <motion.div
          animate={{ x: ["0%", "-50%"] }}
          transition={{ duration: 60, ease: "linear", repeat: Infinity }}
          className="flex gap-6 w-max"
        >
          {loop.map((h, i) => (
            <HighlightCard key={i} data={h} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// Card de resalte — imita una plantilla real de hyperframes
function HighlightCard({ data }) {
  const styles = {
    nombre: { bg: "#0A0A0A", accent: C.gold, layout: "lower" },
    fecha: { bg: C.gold, accent: "#0A0A0A", layout: "date" },
    dato: { bg: "#121212", accent: C.gold, layout: "data" },
    cita: { bg: "#0A0A0A", accent: C.gold, layout: "quote" },
    prensa: { bg: "#F5F1E8", accent: "#0A0A0A", layout: "press" },
    revelacion: { bg: "#0A0A0A", accent: C.gold, layout: "reveal" },
    sello: { bg: "#0A0A0A", accent: "#7B1E1E", layout: "stamp" },
    capitulo: { bg: "#0A0A0A", accent: C.gold, layout: "chapter" },
    estadistica: { bg: "#0A0A0A", accent: C.gold, layout: "stat" },
    mapa: { bg: "#0A0A0A", accent: C.gold, layout: "map" },
  };
  const s = styles[data.tipo] || styles.nombre;
  return (
    <div className="w-[260px] sm:w-[320px] h-[160px] sm:h-[180px] shrink-0 rounded-lg overflow-hidden relative"
      style={{ backgroundColor: s.bg, border: `1px solid rgba(201,169,97,0.15)` }}>
      {/* Hairline top */}
      <div className="absolute top-0 left-6 right-6 h-px" style={{ backgroundColor: s.accent, opacity: 0.6 }} />
      <div className="p-5 h-full flex flex-col justify-end">
        <div className="text-[10px] font-bold tracking-[0.3em] uppercase mb-2"
          style={{ color: s.accent, fontFamily: "'IBM Plex Mono', monospace" }}>{data.kicker}</div>
        <div className="text-xl font-black leading-tight"
          style={{ fontFamily: "'Playfair Display', serif", color: s.bg === "#F5F1E8" ? "#0A0A0A" : C.cream }}>
          {data.texto}
        </div>
        {data.subtitulo && (
          <div className="text-xs mt-1" style={{ color: s.bg === "#F5F1E8" ? "#666" : C.muted }}>
            {data.subtitulo}
          </div>
        )}
      </div>
      {/* Accent bar left */}
      <div className="absolute top-5 bottom-5 left-2 w-0.5" style={{ backgroundColor: s.accent }} />
    </div>
  );
}

// Motor multi-fuente
const SOURCES = [
  { key: "wikimedia", name: "Wikimedia" }, { key: "pexels", name: "Pexels" },
  { key: "unsplash", name: "Unsplash" }, { key: "pixabay", name: "Pixabay" },
  { key: "coverr", name: "Coverr" }, { key: "openverse", name: "Openverse" },
  { key: "archive", name: "Internet Archive" }, { key: "web", name: "Web Search" },
  { key: "ia", name: "IA · Gemini" },
];
function MultiSource({ t }) {
  return (
    <section id="sources" className="py-24 lg:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Kicker>{t("sources.kicker")}</Kicker>
          <H2 className="mb-4 max-w-3xl">{t("sources.title")}</H2>
          <Lead className="mb-16">{t("sources.subtitle")}</Lead>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">
          {SOURCES.map((s, i) => (
            <Reveal key={s.key} delay={i * 0.04}>
              <div data-testid={`source-${s.key}`}
                className="p-6 rounded-xl border transition-all hover:scale-[1.02]"
                style={{ backgroundColor: C.surf, borderColor: "rgba(168,162,154,0.2)" }}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: C.gold }} />
                  <div className="text-xs font-semibold tracking-widest uppercase"
                    style={{ color: C.gold, fontFamily: "'IBM Plex Mono', monospace" }}>
                    {t(`sources.type.${s.key}`)}
                  </div>
                </div>
                <div className="text-2xl font-bold mb-1" style={{ fontFamily: "'Playfair Display', serif", color: C.cream }}>{s.name}</div>
                <div className="text-sm" style={{ color: C.muted }}>{t(`sources.desc.${s.key}`)}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// Antes / Después
function BeforeAfter({ t }) {
  return (
    <section className="py-24 lg:py-32 relative overflow-hidden border-y"
      style={{ borderColor: "rgba(168,162,154,0.15)", backgroundColor: C.surf }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Kicker>{t("compare.kicker")}</Kicker>
          <H2 className="mb-4 max-w-3xl">{t("compare.title")}</H2>
          <Lead className="mb-14">{t("compare.subtitle")}</Lead>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-8">
          {["before", "after"].map((k, idx) => (
            <Reveal key={k} delay={idx * 0.1}>
              <div className="relative rounded-xl overflow-hidden border"
                style={{ borderColor: k === "after" ? "rgba(201,169,97,0.5)" : "rgba(168,162,154,0.2)" }}>
                <div className="aspect-[16/9] flex items-center justify-center relative"
                  style={{ backgroundColor: "#000" }}>
                  {/* Fake video preview */}
                  {k === "before" ? (
                    <div className="text-center px-8">
                      <div className="text-6xl mb-4 opacity-30" style={{ color: "#888" }}>▶</div>
                      <div className="text-lg font-bold" style={{ color: "#888" }}>{t("compare.beforeVideoTitle")}</div>
                      <div className="text-sm mt-2" style={{ color: "#555" }}>Generic Stock · No overlays</div>
                    </div>
                  ) : (
                    <>
                      <img src="/landing-shots/06-editor-broll.png" alt="After" className="absolute inset-0 w-full h-full object-cover opacity-40" />
                      <div className="relative text-center px-8">
                        <div className="text-xs font-bold tracking-[0.3em] mb-2" style={{ color: C.gold, fontFamily: "'IBM Plex Mono', monospace" }}>REVELACIÓN</div>
                        <div className="text-4xl font-black" style={{ fontFamily: "'Playfair Display', serif", color: C.cream }}>{t("compare.afterOverlay")}</div>
                        <div className="text-sm mt-2" style={{ color: C.muted }}>YTMethod · Netflix Docs Style</div>
                      </div>
                    </>
                  )}
                  {/* Cinemascope */}
                  <div className="absolute inset-x-0 top-0 h-3" style={{ backgroundColor: C.bg }} />
                  <div className="absolute inset-x-0 bottom-0 h-3" style={{ backgroundColor: C.bg }} />
                </div>
                <div className="p-6" style={{ backgroundColor: C.bg }}>
                  <div className="text-xs font-bold tracking-[0.3em] uppercase mb-2"
                    style={{ color: k === "after" ? C.gold : C.muted, fontFamily: "'IBM Plex Mono', monospace" }}>
                    {t(`compare.${k}Label`)}
                  </div>
                  <div className="text-xl font-bold mb-2" style={{ fontFamily: "'Playfair Display', serif", color: C.cream }}>{t(`compare.${k}Title`)}</div>
                  <ul className="space-y-1.5 text-sm" style={{ color: C.muted }}>
                    {t(`compare.${k}Points`, { returnObjects: true }).map((p, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span style={{ color: k === "after" ? C.gold : "#555" }}>{k === "after" ? "✓" : "·"}</span>
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// Precios — plans reales del backend con plazas semanales en vivo
const REAL_PLANS = [
  { key: "gratis", price: "0€", period: "", featured: false, minutes: 10, videoMin: 5, channels: 1, storage: "0.5 GB" },
  { key: "starter", price: "19€", period: "/ mes", featured: false, minutes: 120, videoMin: 20, channels: 1, storage: "5 GB" },
  { key: "creator", price: "49€", period: "/ mes", featured: true, minutes: 500, videoMin: 40, channels: 3, storage: "20 GB" },
  { key: "pro", price: "99€", period: "/ mes", featured: false, minutes: 1200, videoMin: 70, channels: 10, storage: "100 GB" },
];

function Pricing({ t, seats, startLogin, loginBusy }) {
  return (
    <section id="pricing" className="py-24 lg:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Kicker>{t("pricing.kicker")}</Kicker>
          <H2 className="mb-4 max-w-3xl">{t("pricing.title")}</H2>
          <Lead className="mb-6">{t("pricing.subtitle")}</Lead>
          <div className="flex items-center gap-2 mb-14 text-xs" style={{ color: C.muted, fontFamily: "'IBM Plex Mono', monospace" }}>
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: "#22c55e" }} />
            <span className="tracking-widest uppercase">{t("pricing.liveIndicator")}</span>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {REAL_PLANS.map((tier, i) => {
            const s = seats?.[tier.key];
            const remaining = s?.remaining ?? null;
            const limit = s?.limit ?? null;
            const almostFull = remaining !== null && limit && remaining <= Math.max(1, Math.floor(limit / 3));
            const soldOut = remaining === 0;
            return (
              <Reveal key={tier.key} delay={i * 0.06}>
                <div data-testid={`pricing-tier-${tier.key}`}
                  className="relative p-6 rounded-2xl border h-full flex flex-col"
                  style={{
                    backgroundColor: C.surf,
                    borderColor: tier.featured ? "rgba(201,169,97,0.6)" : "rgba(168,162,154,0.2)",
                    boxShadow: tier.featured ? `0 20px 60px -20px ${C.gold}40` : "none",
                  }}>
                  {tier.featured && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase whitespace-nowrap"
                      style={{ backgroundColor: C.gold, color: C.bg, fontFamily: "'IBM Plex Mono', monospace" }}>
                      {t("pricing.mostPopular")}
                    </div>
                  )}
                  <div className="text-xs font-bold tracking-[0.3em] uppercase mb-3"
                    style={{ color: C.gold, fontFamily: "'IBM Plex Mono', monospace" }}>{t(`pricing.plans.${tier.key}.tag`)}</div>
                  <div className="text-2xl font-black mb-2" style={{ fontFamily: "'Playfair Display', serif", color: C.cream }}>{t(`pricing.plans.${tier.key}.name`)}</div>
                  <div className="mb-4">
                    <span className="text-4xl font-black" style={{ color: C.cream }}>{tier.price}</span>
                    <span className="text-sm ml-1" style={{ color: C.muted }}>{tier.period}</span>
                  </div>
                  <div className="text-sm mb-4" style={{ color: C.muted }}>{t(`pricing.plans.${tier.key}.description`)}</div>

                  {/* Plazas semanales en vivo */}
                  {remaining !== null && (
                    <div className="mb-4 p-2.5 rounded-lg border"
                      style={{
                        borderColor: soldOut ? "rgba(239,68,68,0.4)" : (almostFull ? "rgba(245,158,11,0.4)" : "rgba(34,197,94,0.3)"),
                        backgroundColor: soldOut ? "rgba(239,68,68,0.05)" : (almostFull ? "rgba(245,158,11,0.05)" : "rgba(34,197,94,0.04)"),
                      }}>
                      <div className="flex items-center justify-between text-xs">
                        <span className="tracking-widest uppercase font-bold" style={{
                          color: soldOut ? "#ef4444" : (almostFull ? "#f59e0b" : "#22c55e"),
                          fontFamily: "'IBM Plex Mono', monospace"
                        }}>
                          {soldOut ? t("pricing.soldOut") : (almostFull ? t("pricing.almostFull") : t("pricing.available"))}
                        </span>
                        <span className="tabular-nums font-bold" style={{ color: C.cream, fontFamily: "'IBM Plex Mono', monospace" }}>
                          {remaining}/{limit}
                        </span>
                      </div>
                      <div className="mt-1.5 h-1 rounded-full overflow-hidden" style={{ backgroundColor: "rgba(168,162,154,0.15)" }}>
                        <div className="h-full transition-all"
                          style={{
                            width: `${100 - (remaining / limit) * 100}%`,
                            backgroundColor: soldOut ? "#ef4444" : (almostFull ? "#f59e0b" : "#22c55e"),
                          }} />
                      </div>
                    </div>
                  )}

                  <ul className="space-y-2 mb-6 flex-1 text-sm">
                    <li className="flex items-start gap-2" style={{ color: C.cream }}>
                      <Check size={14} style={{ color: C.gold, flexShrink: 0, marginTop: 3 }} />
                      <span><b>{fmtCredits(tier.minutes * 60)} {t("common.credits", { count: tier.minutes * 60 })}</b> {t("pricing.features.perMonth")}</span>
                    </li>
                    <li className="flex items-start gap-2" style={{ color: C.cream }}>
                      <Check size={14} style={{ color: C.gold, flexShrink: 0, marginTop: 3 }} />
                      <span>{t("pricing.features.videoMax", { min: tier.videoMin })}</span>
                    </li>
                    <li className="flex items-start gap-2" style={{ color: C.cream }}>
                      <Check size={14} style={{ color: C.gold, flexShrink: 0, marginTop: 3 }} />
                      <span>{t("pricing.features.channels", { count: tier.channels })}</span>
                    </li>
                    <li className="flex items-start gap-2" style={{ color: C.cream }}>
                      <Check size={14} style={{ color: C.gold, flexShrink: 0, marginTop: 3 }} />
                      <span>{tier.storage} {t("pricing.features.storage")}</span>
                    </li>
                    {tier.key !== "gratis" && (
                      <li className="flex items-start gap-2" style={{ color: C.cream }}>
                        <Check size={14} style={{ color: C.gold, flexShrink: 0, marginTop: 3 }} />
                        <span>{t("pricing.features.noWatermark")}</span>
                      </li>
                    )}
                  </ul>
                  <button type="button" onClick={startLogin} disabled={soldOut || loginBusy}
                    className="block w-full text-center py-2.5 rounded-full text-sm font-bold transition-all hover:scale-[1.02] disabled:hover:scale-100"
                    style={soldOut
                      ? { border: `1px solid rgba(168,162,154,0.3)`, color: C.muted }
                      : (tier.featured
                        ? { backgroundColor: C.gold, color: C.bg, boxShadow: `0 8px 30px -10px ${C.gold}`, opacity: loginBusy ? 0.6 : 1 }
                        : { border: `1px solid ${C.gold}`, color: C.gold, opacity: loginBusy ? 0.6 : 1 })}>
                    {soldOut ? t("pricing.waitlist") : t(`pricing.plans.${tier.key}.cta`)}
                  </button>
                </div>
              </Reveal>
            );
          })}
        </div>
        <div className="mt-8 text-center text-xs" style={{ color: C.muted, fontFamily: "'IBM Plex Mono', monospace" }}>
          {t("pricing.seatsExplanation")}
        </div>
      </div>
    </section>
  );
}

// Testimonios
function Testimonials({ t }) {
  const items = t("testimonials.items", { returnObjects: true });
  return (
    <section className="py-24 lg:py-32 relative border-y"
      style={{ borderColor: "rgba(168,162,154,0.15)", backgroundColor: C.surf }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Kicker>{t("testimonials.kicker")}</Kicker>
          <H2 className="mb-14 max-w-3xl">{t("testimonials.title")}</H2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {items.map((it, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div className="p-8 rounded-xl border h-full"
                style={{ backgroundColor: C.bg, borderColor: "rgba(168,162,154,0.15)" }}>
                <div className="flex mb-4">
                  {[...Array(5)].map((_, s) => <Star key={s} size={14} style={{ color: C.gold, fill: C.gold }} />)}
                </div>
                <p className="text-lg leading-relaxed mb-6 italic"
                  style={{ fontFamily: "'Playfair Display', serif", color: C.cream }}>
                  "{it.quote}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold"
                    style={{ backgroundColor: C.gold, color: C.bg, fontFamily: "'IBM Plex Mono', monospace" }}>
                    {it.name.split(" ").map(n => n[0]).join("").slice(0, 2)}
                  </div>
                  <div>
                    <div className="font-bold text-sm" style={{ color: C.cream }}>{it.name}</div>
                    <div className="text-xs" style={{ color: C.muted }}>{it.role}</div>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// FAQ
function FAQ({ t }) {
  const [open, setOpen] = useState(null);
  const items = t("faq.items", { returnObjects: true });
  return (
    <section id="faq" className="py-24 lg:py-32">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Kicker>{t("faq.kicker")}</Kicker>
          <H2 className="mb-14">{t("faq.title")}</H2>
        </Reveal>
        <div className="space-y-2">
          {items.map((q, i) => (
            <Reveal key={i} delay={i * 0.03}>
              <div className="border-b" style={{ borderColor: "rgba(168,162,154,0.15)" }}>
                <button
                  data-testid={`faq-toggle-${i}`}
                  onClick={() => setOpen(open === i ? null : i)}
                  className="w-full flex items-center justify-between py-5 text-left transition-colors">
                  <span className="text-lg font-semibold pr-4" style={{ color: C.cream }}>{q.q}</span>
                  <ChevronDown size={20} style={{ color: C.gold, transition: "transform 0.3s", transform: open === i ? "rotate(180deg)" : "rotate(0)" }} />
                </button>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    className="overflow-hidden">
                    <p className="pb-6 pr-8 text-base leading-relaxed" style={{ color: C.muted }}>{q.a}</p>
                  </motion.div>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// CTA final
function FinalCTA({ t, startLogin, loginBusy }) {
  return (
    <section className="py-24 lg:py-32 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: `radial-gradient(circle at 50% 50%, rgba(201,169,97,0.12) 0%, transparent 60%)` }} />
      <div className="relative max-w-4xl mx-auto px-6 text-center">
        <Reveal>
          <H2 className="mb-6">{t("finalCta.title")}</H2>
          <p className="text-xl mb-10 max-w-2xl mx-auto" style={{ color: C.muted }}>{t("finalCta.subtitle")}</p>
          <button type="button" onClick={startLogin} disabled={loginBusy} data-testid="final-cta"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-lg font-bold hover:scale-[1.02] transition-transform disabled:opacity-60"
            style={{ backgroundColor: C.gold, color: C.bg, boxShadow: `0 20px 60px -20px ${C.gold}` }}>
            <Wand2 size={20} /> {t("finalCta.button")}
          </button>
        </Reveal>
      </div>
    </section>
  );
}

// Footer
function Footer({ t, startLogin, loginBusy }) {
  return (
    <footer className="border-t py-12" style={{ borderColor: "rgba(168,162,154,0.15)", backgroundColor: C.bg }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-4 gap-8">
        <div className="md:col-span-2">
          <img src="/ytmethod_transparent.png" alt="YTMethod" className="h-10 w-auto mb-4" style={{ filter: "brightness(0) invert(1)" }} />
          <p className="text-sm max-w-sm" style={{ color: C.muted }}>{t("footer.tagline")}</p>
          <div className="flex gap-4 mt-6">
            {[Youtube, Twitter, Instagram, Mail].map((Icon, i) => (
              <a key={i} href="#" className="hover:scale-110 transition-transform" style={{ color: C.muted }} data-testid={`social-${i}`}>
                <Icon size={20} />
              </a>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs font-bold tracking-widest uppercase mb-4" style={{ color: C.gold, fontFamily: "'IBM Plex Mono', monospace" }}>{t("footer.product")}</div>
          <ul className="space-y-2 text-sm" style={{ color: C.muted }}>
            <li><a href="#how" className="hover:text-white">{t("nav.how")}</a></li>
            <li><a href="#pricing" className="hover:text-white">{t("nav.pricing")}</a></li>
            <li><button type="button" onClick={startLogin} disabled={loginBusy} className="hover:text-white disabled:opacity-60">{t("footer.login")}</button></li>
          </ul>
        </div>
        <div>
          <div className="text-xs font-bold tracking-widest uppercase mb-4" style={{ color: C.gold, fontFamily: "'IBM Plex Mono', monospace" }}>{t("footer.legal")}</div>
          <ul className="space-y-2 text-sm" style={{ color: C.muted }}>
            <li><Link to="/privacidad" className="hover:text-white">{t("footer.privacy")}</Link></li>
            <li><Link to="/terminos" className="hover:text-white">{t("footer.terms")}</Link></li>
          </ul>
        </div>
      </div>
      <Hairline className="max-w-7xl mx-auto mt-12 mb-6" />
      <div className="max-w-7xl mx-auto px-6 text-xs" style={{ color: C.muted }}>© 2026 YTMethod · {t("footer.rights")}</div>
    </footer>
  );
}

export default function CreatorsLanding() {
  useLoadFonts();
  const { t, i18n } = useTranslation("translation", { keyPrefix: "creators" });
  // t() de arriba lleva el prefijo "creators." (todo el copy propio de esta landing) — los
  // mensajes de auth/lista de espera son compartidos con el resto de la app (viven en
  // auth.*/landing.*, sin prefijo), de ahí este segundo traductor sin keyPrefix.
  const { t: tg } = useTranslation();
  const [lang, setLangState] = useState(i18n.language?.startsWith("en") ? "en" : "es");
  const [seats, setSeats] = useState(null);
  const setLang = (l) => { setLangState(l); i18n.changeLanguage(l); };

  useEffect(() => {
    document.title = "YTMethod — " + t("meta.title");
  }, [t]);

  // Mismo patrón que Landing.jsx: los CTA van directos a Google en vez de pasar por /login.
  // Esta landing no tiene concepto de "plan pendiente" (a diferencia de Landing.jsx, aquí
  // ningún CTA fija un plan concreto antes de entrar), así que no hace falta replicar setPlan.
  const { loginWithGoogle, apiErr } = useAuth();
  const navigate = useNavigate();
  const [loginBusy, setLoginBusy] = useState(false);
  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async ({ code }) => {
      setLoginBusy(true);
      try { await loginWithGoogle(code); }
      catch (e2) {
        const d = e2?.response?.data?.detail;
        if (d?.error === "seats_full") {
          toast.info(tg("landing.seatsFullRedirect"));
          navigate("/login");
        } else {
          toast.error(apiErr(e2));
        }
      } finally { setLoginBusy(false); }
    },
    onError: () => toast.error(tg("auth.googleError")),
  });
  const startLogin = () => googleLogin();

  // Carga las plazas semanales reales de /api/plan-seats para mostrar disponibilidad en vivo
  // en las tarjetas de precio. Los cupos son los WEEKLY_PLAN_SEATS del backend: gratis 15,
  // starter 5, creator 3, pro 2 por semana. Si falla la llamada, dejamos `seats=null` y las
  // tarjetas se pintan sin el indicador (no rompe la landing).
  useEffect(() => {
    const url = (process.env.REACT_APP_BACKEND_URL || "") + "/api/plan-seats";
    fetch(url)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => d && setSeats(d.plans || null))
      .catch(() => {});
  }, []);

  return (
    <div style={{ backgroundColor: C.bg, color: C.cream, fontFamily: "'Inter', sans-serif", minHeight: "100vh" }}>
      <Header lang={lang} setLang={setLang} t={t} startLogin={startLogin} loginBusy={loginBusy} />
      <Hero t={t} startLogin={startLogin} loginBusy={loginBusy} />
      <HowItWorks t={t} />
      <HighlightsGallery t={t} />
      <MultiSource t={t} />
      <BeforeAfter t={t} />
      <Pricing t={t} seats={seats} startLogin={startLogin} loginBusy={loginBusy} />
      <Testimonials t={t} />
      <FAQ t={t} />
      <FinalCTA t={t} startLogin={startLogin} loginBusy={loginBusy} />
      <Footer t={t} startLogin={startLogin} loginBusy={loginBusy} />
    </div>
  );
}
